// Trade counters near a postcode the engineer typed.
//
// DEV-51045 rules this out ("a typed postcode doesn't trigger it"), on the
// grounds that typing says where you want to shop, not where you are. Offered
// anyway by decision, with the wording kept honest throughout: a typed
// postcode resolves to the CENTRE of a postcode unit, which can sit a couple
// of hundred metres from any given building, so this only ever claims a
// counter is near the area — never that the engineer is standing in it.
//
// The device route is untouched and remains the accurate one.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { useDebounce } from './useDebounce'
import { api } from '../services/api'
import { initialAtBranchSelection } from '../services/engineerSession'
import { lookupPostcode, normalizePostcode } from '../services/postcodes'
import type { BranchAtPosition } from '../services/api'
import type { CurrentAtBranches } from '../services/engineerSession'

// Long enough that the lookup runs once the engineer has stopped typing
// rather than once per keystroke. Deliberately slower than the suggestion
// list: this fires two requests, and nothing waits on its answer.
const LOOKUP_DEBOUNCE_MS = 600

// Shortest possible UK postcode ("M1 1AA" without its space). Anything
// shorter cannot resolve, so it is not worth a request.
const MIN_POSTCODE_LENGTH = 5

export interface UseBranchesNearPostcodeResult {
  branches: BranchAtPosition[]
  /** The branches chosen to show first, any number of them. */
  selectedBranchIds: string[]
  setSelectedBranchIds: (branchIds: string[]) => void
}

/**
 * @param postcode What is currently in the postcode field.
 * @param enabled False while the device route owns the card, so the two
 *   never offer counters at the same time.
 * @param current The postcode in use and its pinned branches, so the card
 *   opens on what's pinned when the field holds that postcode.
 */
export function useBranchesNearPostcode(
  postcode: string,
  enabled: boolean,
  current: CurrentAtBranches | null,
): UseBranchesNearPostcodeResult {
  const trimmedPostcode = postcode.trim()
  const debouncedPostcode = useDebounce(trimmedPostcode, LOOKUP_DEBOUNCE_MS)
  const isWorthLooking =
    enabled && debouncedPostcode.replace(/\s+/g, '').length >= MIN_POSTCODE_LENGTH

  // The debounced value lags the field by LOOKUP_DEBOUNCE_MS. Anything it
  // found describes the postcode as it was, so it must not be offered once
  // the engineer has moved on: confirming inside that window would otherwise
  // save the new postcode with a counter from the old area.
  const describesCurrentPostcode =
    normalizePostcode(debouncedPostcode) === normalizePostcode(trimmedPostcode)

  const { data: branches = [] } = useQuery({
    queryKey: ['branches-near-postcode', debouncedPostcode.toUpperCase()],
    queryFn: async ({ signal }) => {
      const coordinates = await lookupPostcode(debouncedPostcode)
      if (!coordinates) return []
      return api.getBranchesAtPosition(
        coordinates.latitude,
        coordinates.longitude,
        signal,
      )
    },
    enabled: isWorthLooking,
    // A postcode's counters don't move, so a repeat within the session is
    // answered from cache rather than two more requests.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  const offered = isWorthLooking && describesCurrentPostcode ? branches : []

  // Default the choice whenever the postcode, the offered set or the pins
  // change (see initialAtBranchSelection). Derived during render (no effect),
  // so the card never paints a stale selection.
  const signature = [
    normalizePostcode(debouncedPostcode),
    current ? `${current.postcode}:${current.branchIds.join(',')}` : '',
    offered.map((branch) => branch.branchId).join('|'),
  ].join('#')
  const defaultIds = initialAtBranchSelection(debouncedPostcode, offered, current)
  const [selection, setSelection] = useState<{
    signature: string
    branchIds: string[]
  }>({ signature, branchIds: defaultIds })
  if (selection.signature !== signature) {
    setSelection({ signature, branchIds: defaultIds })
  }

  return {
    branches: offered,
    selectedBranchIds: selection.branchIds,
    setSelectedBranchIds: (branchIds) => setSelection({ signature, branchIds }),
  }
}
